/**
 * useChat — SSE streaming with typed events, abort, retry.
 * Replaces ~300 lines across api.js + generate.js.
 *
 * Handles: token buffering, phase updates, abort via AbortController,
 * error handling.
 */
import { useState, useCallback, useRef } from 'react';
import { useStore } from '../store';
import { streamSSE } from '../utils/sse';
import CONFIG from '../config';
import Logger from '../utils/logger';
import type { AgentResponse, AgentPhase, AllowanceState, RequestHeaders } from '../types';

interface ApiErrorPayload {
  error?: string | { message?: string; retry_after_seconds?: number };
  code?: string;
  retry_after?: number;
  retry_after_seconds?: number;
  allowance?: unknown;
}

function isAllowanceState(value: unknown): value is AllowanceState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  const numericFields = [
    'limit',
    'used',
    'reserved',
    'remaining',
    'window_seconds',
    'next_reset_in_seconds',
    'retry_after_seconds',
  ];
  return (
    numericFields.every((field) => typeof candidate[field] === 'number') &&
    (candidate.next_reset_at === null || typeof candidate.next_reset_at === 'number')
  );
}

export interface UseChatReturn {
  /** Accumulated tokens from current stream */
  tokens: string;
  /** Whether a stream is currently active */
  isStreaming: boolean;
  /** Current agent phase (thinking, analyzing, etc.) */
  phase: AgentPhase | null;
  /** Last error from streaming */
  error: Error | null;
  /** Agent response metadata from last stream */
  agentResponse: AgentResponse | null;
  /** Latest server-authoritative free-message allowance */
  allowance: AllowanceState | null;
  cooldownUntil: number;
  /** Get current tokens (ref-based, avoids stale closures) */
  getTokens: () => string;
  /** Send a prompt to the backend. Returns success/error to avoid stale closure issues. */
  send: (
    prompt: string,
    buildAuthHeaders: (endpoint: string) => Promise<RequestHeaders | null>
  ) => Promise<{
    success: boolean;
    error?: string;
    chatId?: string;
    assistantMessageId?: number | null;
  }>;
  /** Refresh the free-message allowance without consuming it. */
  refreshAllowance: (
    buildAuthHeaders: (endpoint: string) => Promise<RequestHeaders | null>
  ) => Promise<AllowanceState | null>;
  /** Abort the current stream */
  stop: () => void;
  /** Clear error state */
  clearError: () => void;
}

export function useChat(): UseChatReturn {
  const [tokens, setTokens] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [phase, setPhase] = useState<AgentPhase | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [agentResponse, setAgentResponse] = useState<AgentResponse | null>(null);
  const [allowance, setAllowance] = useState<AllowanceState | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const edgeAllowanceRef = useRef<AllowanceState | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const tokensRef = useRef('');
  const sessionChatIdRef = useRef<string | null>(null);
  const assistantMessageIdRef = useRef<number | null>(null);
  const streamErrorRef = useRef<Error | null>(null);
  const streamCompletedRef = useRef(false);

  /** Get current tokens (ref-based, avoids stale closures) */
  const getTokens = useCallback(() => tokensRef.current, []);

  const setGenerating = useStore((s) => s.setGenerating);

  /** Process SSE events from the stream */
  const processEvents = useCallback(
    async function* (
      response: Response,
      signal: AbortSignal
    ): AsyncGenerator<void, void, unknown> {
      for await (const event of streamSSE(response, signal)) {
        if (event.type === 'session' && event.chat_id) {
          sessionChatIdRef.current = event.chat_id;
          useStore.getState().setCurrentChatId(event.chat_id);
        }

        // Phase updates
        if (event.phase) {
          setPhase({ name: event.phase, message: event.message });
        }

        // Token updates
        if (event.token) {
          tokensRef.current += event.token;
          setTokens(tokensRef.current);
        }

        // Completion
        if (event.done) {
          streamCompletedRef.current = true;
          if (edgeAllowanceRef.current) {
            setAllowance(edgeAllowanceRef.current);
          } else if (isAllowanceState(event.allowance)) {
            setAllowance(event.allowance);
          }
          assistantMessageIdRef.current = event.assistant_message_id ?? null;
          const agentResp: AgentResponse = {
            done_reason: event.done_reason ?? 'stop',
            ...(event.response ?? {}),
          };
          setAgentResponse(agentResp);
          // Unlock UI immediately on protocol-level completion.
          // Socket teardown can lag briefly after final token/done event.
          setIsStreaming(false);
          setGenerating(false);
          return;
        }

        // Error from backend
        if (event.error) {
          const streamError = new Error(event.error);
          streamErrorRef.current = streamError;
          setError(streamError);
        }

        yield;
      }
    },
    []
  );

  /** Send a prompt to the agent endpoint */
  const send = useCallback(
    async (
      prompt: string,
      buildAuthHeaders: (endpoint: string) => Promise<RequestHeaders | null>
    ): Promise<{
      success: boolean;
      error?: string;
      chatId?: string;
      assistantMessageId?: number | null;
    }> => {
      if (abortRef.current || Date.now() < cooldownUntil) {
        return { success: false, error: 'Please wait before sending another message.' };
      }
      // Reset state
      abortRef.current = new AbortController();
      setIsStreaming(true);
      setTokens('');
      setPhase(null);
      setError(null);
      setAgentResponse(null);
      tokensRef.current = '';
      sessionChatIdRef.current = null;
      assistantMessageIdRef.current = null;
      streamErrorRef.current = null;
      streamCompletedRef.current = false;
      edgeAllowanceRef.current = null;
      setGenerating(true);

      try {
        const headers = await buildAuthHeaders('/generate/agent');
        if (!headers) throw new Error('Unable to start this chat session.');

        const state = useStore.getState();

        // AppShell has already appended the current optimistic user message.
        // Send completed earlier turns separately so guest context also follows
        // the visible chat after an edit or a model-origin restart.
        const history = state.chatHistory.slice();
        const latest = history[history.length - 1];
        if (latest?.role === 'user' && latest.content === prompt) history.pop();

        const body = {
          prompt,
          chat_id: state.currentChatId ?? undefined,
          history: history.slice(-200).map(({ role, content }) => ({ role, content })),
        };

        const response = await fetch(`${CONFIG.API_URL}/generate/agent`, {
          method: 'POST',
          signal: abortRef.current.signal,
          credentials: 'include',
          headers,
          body: JSON.stringify(body),
        });
        try {
          const edgeAllowance = JSON.parse(response.headers.get('X-W-AI-Allowance') || 'null');
          if (isAllowanceState(edgeAllowance)) {
            edgeAllowanceRef.current = edgeAllowance;
            setAllowance(edgeAllowance);
          }
        } catch { /* Older backends report their allowance in the terminal event. */ }

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('This chat session could not be verified. Please refresh the page.');
          }
          // Handle rate limiting
          if (response.status === 429) {
            const data = (await response.json().catch(() => ({}))) as ApiErrorPayload;
            const message = typeof data.error === 'string' ? data.error : data.error?.message;
            const retryAfter = data.retry_after || data.retry_after_seconds ||
              (typeof data.error === 'object' ? data.error.retry_after_seconds : 0) ||
              Number(response.headers.get('Retry-After')) || 3;
            setCooldownUntil(Date.now() + retryAfter * 1000);
            if (data.code === 'free_allowance_exhausted') {
              if (isAllowanceState(data.allowance)) {
                setAllowance(data.allowance);
              }
              throw new Error(message || 'Daily message limit reached. Your messages will renew automatically.');
            }
            throw new Error(
              `${message || 'Please slow down.'} Try again in ${Math.ceil(retryAfter)}s.`
            );
          }
          throw new Error(`Request failed: ${response.status} ${response.statusText}`);
        }

        // Process the event stream
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for await (const _event of processEvents(response, abortRef.current.signal)) {
          // Events processed by processEvents
        }

        if (streamErrorRef.current) {
          throw streamErrorRef.current;
        }
        if (!streamCompletedRef.current) {
          throw new Error('The response ended before it completed. Please try again.');
        }

        return {
          success: true,
          chatId: sessionChatIdRef.current ?? (useStore.getState().currentChatId ?? undefined),
          assistantMessageId: assistantMessageIdRef.current,
        };
      } catch (e) {
        const err = e as Error;
        if (err.name !== 'AbortError') {
          setError(err);
          Logger.error('Chat send error:', err);
          return { success: false, error: err.message };
        }
        return { success: false, error: 'Aborted' };
      } finally {
        setIsStreaming(false);
        setGenerating(false);
        abortRef.current = null;
      }
    },
    [setGenerating, processEvents, cooldownUntil]
  );

  const refreshAllowance = useCallback(
    async (
      buildAuthHeaders: (endpoint: string) => Promise<RequestHeaders | null>
    ): Promise<AllowanceState | null> => {
      try {
        const endpoint = '/generate/allowance';
        const headers = await buildAuthHeaders(endpoint);
        if (!headers) return null;

        const response = await fetch(`${CONFIG.API_URL}${endpoint}`, {
          method: 'GET',
          headers,
        });
        if (!response.ok) {
          Logger.warn(`Allowance refresh failed: ${response.status}`);
          return null;
        }

        const data = (await response.json()) as { allowance?: unknown };
        if (!isAllowanceState(data.allowance)) {
          Logger.warn('Allowance refresh returned an invalid payload');
          return null;
        }

        setAllowance(data.allowance);
        return data.allowance;
      } catch (refreshError) {
        Logger.warn('Allowance refresh failed:', refreshError);
        return null;
      }
    },
    []
  );

  /** Abort current stream */
  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /** Clear error state */
  const clearError = useCallback(() => setError(null), []);

  return {
    tokens,
    isStreaming,
    phase,
    error,
    agentResponse,
    allowance,
    cooldownUntil,
    getTokens,
    send,
    refreshAllowance,
    stop,
    clearError,
  };
}

export default useChat;
